import { IDeliveryInformationConfig, IPatient } from '@alberta/konexi-shared';
import { Injectable } from '@angular/core';
import { assignIn } from 'lodash';
import { PatientComponentModel } from 'src/app/business/patient/patients-component-model';
import { PatientViewModel } from 'src/app/business/patient/view-models/patient-view-model';
import { PatientDB } from 'src/app/common/repository/databases';
import { observe } from 'src/app/common/tracking';
import { SelectionItem } from 'src/app/shared/models/selectionItem';
import { QueryService } from 'src/app/shared/services/query/query.service';
import { ActionEvent, TrackerService } from 'src/app/shared/services/tracker.service';

import { map } from '@common/mapper/mapper';
import { PatientDto } from 'src/app/shared/models/patient/patient-dto.model';
import { AlertDialogService } from '../alert-dialog-service';
import { SeriesDataViewModel } from '../integrated-care/series-data-view-model';
import { SingleOrderViewModel } from '../single-order/single-order-view-model';
import { TenantConfigService } from './../../shared/services/tenant-config/tenant-config.service';
import { OrderViewModel } from './view-models/order-view-model';
import { SeriesOrderViewModel } from './view-models/series-order-viewmodel';

@Injectable()
export class OrderDeliveryInformationService {
  constructor(
    private _queryService: QueryService,
    private _patientComponentModel: PatientComponentModel,
    private _alertDialogService: AlertDialogService,
    private _tracker: TrackerService,
    private _tenantConfigService: TenantConfigService
  ) {}

  async saveDeliveryInformationToPatient(order: SingleOrderViewModel | SeriesDataViewModel, patientId: string) {
    const tenantConfig = await this._tenantConfigService.getTenantConfig();
    const patient = await this._queryService.get<IPatient>(patientId, PatientDB);
    const patientVM = observe(assignIn(new PatientViewModel(), patient)) as PatientViewModel;
    let patientChanged = false;
    const startTime = this.getStartTime(order);
    const showDeliveryInformation = await this._tenantConfigService.showPatientDeliveryInformation();
    const hasPatientDeliveryNote = await this._tenantConfigService.hasPatientDeliveryNote();
    const showPatientDeliveryOptions = tenantConfig.patientData?.deliveryOption?.show;

    if (showDeliveryInformation && order.deliveryInformation) {
      if (
        patientVM.deliveryInformation?.deliveryInformation !== order.deliveryInformation ||
        patientVM.deliveryInformation?.startTime !== startTime
      ) {
        this._tracker.trackDeliveryInformationMismatch(ActionEvent.Start, 'deliveryInformation');
        const changeDeliveryInformation = await this._alertDialogService.showYesNoAlert(
          'order.changeDeliveryInformation',
          'order.changeDeliveryInformation_text'
        );
        if (changeDeliveryInformation) {
          patientChanged = true;
          patientVM.deliveryInformation.deliveryInformation = order.deliveryInformation;
          patientVM.deliveryInformation.startTime = startTime;
        }
      }
    }
    if (
      hasPatientDeliveryNote &&
      order.deliveryNote &&
      patientVM.deliveryInformation?.deliveryNote !== order.deliveryNote
    ) {
      this._tracker.trackDeliveryInformationMismatch(ActionEvent.Start, 'deliveryNote');
      const changeDeliveryNote = await this._alertDialogService.showYesNoAlert(
        'order.changeDeliveryNote',
        'order.changeDeliveryNote_text'
      );
      if (changeDeliveryNote) {
        patientChanged = true;
        patientVM.deliveryInformation.deliveryNote = order.deliveryNote;
      }
    }

    if (
      showPatientDeliveryOptions &&
      order.deliveryOption &&
      patientVM.deliveryInformation?.deliveryOption !== order.deliveryOption
    ) {
      this._tracker.trackDeliveryInformationMismatch(ActionEvent.Start, 'deliveryOption');
      const changeDeliveryOption = await this._alertDialogService.showYesNoAlert(
        'order.changeDeliveryOption',
        'order.changeDeliveryOption_text'
      );
      if (changeDeliveryOption) {
        patientChanged = true;
        patientVM.deliveryInformation.deliveryOption = (order as SingleOrderViewModel).deliveryOption;
      }
    }

    if (patientChanged) {
      this._tracker.trackDeliveryInformationMismatchSave(ActionEvent.Done);
      await this._patientComponentModel.edit(patientVM, map(patientVM, new PatientDto()));
    }
  }

  public createDeliveryInformationSelectionItems(
    deliveryInformationOptions: IDeliveryInformationConfig[]
  ): SelectionItem[] {
    return (deliveryInformationOptions ?? []).map(item => new SelectionItem(item.name, item.name));
  }

  public getSelectedDeliveryInformationOption(
    deliveryInformation: string,
    deliveryInformationOptions: IDeliveryInformationConfig[]
  ): IDeliveryInformationConfig {
    const selectedDeliveryInformationOption = deliveryInformationOptions.find(
      deliveryInformationOption => deliveryInformationOption.name === deliveryInformation
    );
    return selectedDeliveryInformationOption;
  }

  private getStartTime(
    order: OrderViewModel | SingleOrderViewModel | SeriesOrderViewModel | SeriesDataViewModel
  ): string {
    if (order instanceof OrderViewModel || order instanceof SeriesOrderViewModel) {
      return order.startTime;
    } else {
      return order.deliveryStartTime;
    }
  }

  public async createDeliveryOptionSelectionItems(): Promise<SelectionItem[]> {
    const deliveryOptions = await this._tenantConfigService.getDeliveryOptions();
    return (deliveryOptions ?? []).map(item => new SelectionItem(item, item));
  }
}
